import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import axios from "axios";
import { Segmented, Select } from "antd";
import dayjs from "dayjs";
import utils from "utils";
const { Option } = Select;

const generateRandomData = () => {
  const getRandomInt = (min, max) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

  const today = dayjs();
  const xAxisData = Array.from({ length: 7 }, (_, i) =>
    today.subtract(i, "day").format("YYYY-MM-DD")
  ).reverse();
  const heartRate = Array.from({ length: 15 }, () => getRandomInt(60, 100));
  const systolicBP = Array.from({ length: 15 }, () => getRandomInt(100, 140));
  const diastolicBP = Array.from({ length: 15 }, () => getRandomInt(60, 90));
  const temperature = Array.from(
    { length: 15 },
    () => getRandomInt(360, 380) / 10
  ); // generates temperature between 36.0°C to 38.0°C

  return {
    xAxisData,
    heartRate,
    systolicBP,
    diastolicBP,
    temperature,
  };
};

const sampleData = generateRandomData();
const vitalLabel = {
  heartRate: "Heart Rate",
  systolicBP: "Systolic Blood Pressure",
  diastolicBP: "Diastolic Blood Pressure",
  temperature: "Temperature",
};
const VitalChart = () => {
  const [xAxisData, setXAxisData] = useState(sampleData.xAxisData);
  const [selectedVital, setSelectedVital] = useState("heartRate");
  const [vitalData, setVitalData] = useState(sampleData[selectedVital]);
  const [range, setRange] = useState("all");

  useEffect(() => {
    fetchData(selectedVital, range);
  }, [selectedVital, range]);

  const fetchData = async (vital, range) => {
    setVitalData(sampleData[vital]);
    try {
      // Replace with your API URL and modify it to fetch only the selected vital sign
      const response = await axios.get(
        `https://api.example.com/vital-signs/${vital}`
      );
      const data = response.data;
      setXAxisData(data.map((item) => item.date));
      setVitalData(data.map((item) => item.value));
    } catch (error) {
      console.error("Error fetching the data", error);
    }
  };

  const handleChange = (value) => {
    setSelectedVital(value);
  };

  const renderSelect = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        paddingBottom: "10px",
      }}
    >
      <Select
        value={selectedVital}
        style={{ width: 200 }}
        onChange={handleChange}
      >
        <Option value="heartRate">Heart Rate</Option>
        <Option value="systolicBP">Systolic Blood Pressure</Option>
        <Option value="diastolicBP">Diastolic Blood Pressure</Option>
        <Option value="temperature">Temperature</Option>
      </Select>
      <Segmented
        size="middle"
        value={range}
        onChange={(tab) => {
          setRange(tab);
        }}
        options={[
          {
            label: <div>All Vitals</div>,
            value: "all",
          },
          {
            label: <div>Last 5 Vitals</div>,
            value: 5,
          },
          {
            label: <div>Last 10 Vitals</div>,
            value: 10,
          },
        ]}
      />
    </div>
  );

  const renderChart = () => {
    if (!xAxisData || !vitalData) return null;

    const unit = getUnit(selectedVital);

    const option = {
      title: {
        text: `${vitalLabel[selectedVital]}`,
        subtext: "Vital Signs",
        left: "center",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
        },
        formatter: (params) => {
          let tooltip = params[0].axisValue + "<br/>";
          params.forEach((item) => {
            tooltip += `${item.marker} ${item.seriesName}: ${item.value} ${unit}<br/>`;
          });
          return tooltip;
        },
      },
      // legend: {
      //   data: [selectedVital],
      //   top: 'bottom'
      // },
      toolbox: {
        feature: {
          saveAsImage: {},
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: true,
        data: xAxisData,
        axisLabel: {
          formatter: (value) =>
            `${utils.FormatDate(value, "TIME_1")},\n${utils.FormatDate(
              value,
              "DATE_14"
            )}`,
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          formatter: (value) => `${value} ${unit}`,
        },
        axisPointer: {
          snap: true,
        },
      },
      series: [
        {
          name: selectedVital,
          type: "line",
          data: vitalData,
          markPoint: {
            data: [
              { type: "max", name: "Max" },
              { type: "min", name: "Min" },
            ],
          },
          markLine: {
            data: [{ type: "average", name: "Average" }],
          },
        },
      ],
    };

    return (
      <ReactECharts
        option={option}
        style={{ height: "400px", width: "100%" }}
      />
    );
  };

  const getUnit = (vital) => {
    switch (vital) {
      case "Heart Rate":
        return "bpm";
      case "Systolic Blood Pressure":
      case "Diastolic Blood Pressure":
        return "mmHg";
      case "Temperature":
        return "°C";
      default:
        return "";
    }
  };

  return (
    <div>
      {renderSelect()}
      {renderChart()}
    </div>
  );
};

export default VitalChart;
